import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { TrendingUp, ShoppingCart, Package, AlertTriangle, Users, Eye } from 'lucide-react';
import { toast } from 'sonner';

// Si la variable de entorno está vacía (Prod), usamos el origen actual (para que funcione el proxy /api)
// Si tiene valor (Dev), usamos ese valor (ej: http://localhost:8000)
const ENV_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
let BACKEND_URL = '';

if (!ENV_BACKEND_URL || ENV_BACKEND_URL === '') {
  // Producción: Usar URL relativa
  BACKEND_URL = window.location.origin; 
} else {
  // Desarrollo: Usar valor explícito
  BACKEND_URL = ENV_BACKEND_URL;
}

// Limpiar si trae /api al final
if (BACKEND_URL.endsWith('/api')) {
  BACKEND_URL = BACKEND_URL.slice(0, -4);
}

const API = `${BACKEND_URL}/api`;

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [salesByRegister, setSalesByRegister] = useState([]);
  const [salesByWarehouse, setSalesByWarehouse] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedRegisterDetails, setSelectedRegisterDetails] = useState(null);
  const [openRegisterDetails, setOpenRegisterDetails] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, salesRes, registersRes, warehousesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/dashboard/recent-sales`),
        axios.get(`${API}/dashboard/sales-by-register?period=today`),
        axios.get(`${API}/dashboard/sales-by-warehouse?period=today`)
      ]);
      setStats(statsRes.data);
      setRecentSales(salesRes.data);
      setSalesByRegister(registersRes.data);
      setSalesByWarehouse(warehousesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos del dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">Resumen general del negocio</p>
      </div>

      {/* Stats Grid - Bento Style */}
      {/* Stats Grid - Bento Style */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="col-span-1 hover:shadow-md transition-shadow" data-testid="today-sales-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ventas Hoy
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight">
              {formatCLP(stats?.today_sales || 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.today_sales_count || 0} transacciones
            </p>
          </CardContent>
        </Card>

        <Card className="col-span-1 hover:shadow-md transition-shadow" data-testid="month-sales-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ventas del Mes
            </CardTitle>
            <ShoppingCart className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight">
              {formatCLP(stats?.month_sales || 0)}
            </div>
          </CardContent>
        </Card>

        <Card className="col-span-1 hover:shadow-md transition-shadow bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900" data-testid="receivables-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-red-800 dark:text-red-300">
              Por Cobrar
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold font-mono tracking-tight text-red-700 dark:text-red-400">
              {formatCLP(stats?.accounts_receivable || 0)}
            </div>
             <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-1">
              Deuda total clientes
            </p>
          </CardContent>
        </Card>

        <Card className="col-span-1 hover:shadow-md transition-shadow" data-testid="low-stock-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Stock Bajo
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold tracking-tight">
              {stats?.low_stock_count || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              productos
            </p>
          </CardContent>
        </Card>

        <Card className="col-span-1 hover:shadow-md transition-shadow" data-testid="customers-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Clientes
            </CardTitle>
            <Users className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold tracking-tight">
              {stats?.total_customers || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales by Register (New) */}
      {salesByRegister.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {salesByRegister.map((reg, idx) => (
                  <Card key={idx} className="bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800">
                      <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-emerald-800 dark:text-emerald-300 flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                              {reg.register_name}
                          </CardTitle>
                      </CardHeader>
                      <CardContent>
                          <div className="text-2xl font-bold font-mono tracking-tight text-emerald-700 dark:text-emerald-400">
                              {formatCLP(reg.total_sales)}
                          </div>
                          <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80 mt-1 mb-2">
                              {reg.transaction_count} ventas hoy
                          </p>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full text-xs h-7 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700 dark:border-emerald-800 dark:hover:bg-emerald-900"
                            onClick={() => {
                                setSelectedRegisterDetails(reg);
                                setOpenRegisterDetails(true);
                            }}
                          >
                              <Eye className="h-3 w-3 mr-1" /> Ver Detalles
                          </Button>
                      </CardContent>
                  </Card>
              ))}
          </div>
      )}

      {/* Register Details Dialog */}
      <Dialog open={openRegisterDetails} onOpenChange={setOpenRegisterDetails}>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>Detalle de Ventas - {selectedRegisterDetails?.register_name}</DialogTitle>
                <DialogDescription>Desglose de métodos de pago</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted p-3 rounded-lg">
                        <span className="text-xs text-muted-foreground block">Total Ventas</span>
                        <span className="text-xl font-bold font-mono text-emerald-600">
                            {formatCLP(selectedRegisterDetails?.total_sales || 0)}
                        </span>
                    </div>
                    <div className="bg-muted p-3 rounded-lg">
                        <span className="text-xs text-muted-foreground block">Transacciones</span>
                        <span className="text-xl font-bold font-mono">
                            {selectedRegisterDetails?.transaction_count || 0}
                        </span>
                    </div>
                </div>
                
                <div className="space-y-2">
                    <h4 className="text-sm font-medium">Métodos de Pago</h4>
                    <div className="border rounded-md divide-y">
                        {selectedRegisterDetails?.payment_methods && Object.entries(selectedRegisterDetails.payment_methods).map(([method, amount]) => (
                            <div key={method} className="flex justify-between p-2 text-sm">
                                <span className="capitalize">{method}</span>
                                <span className="font-mono font-medium">{formatCLP(amount)}</span>
                            </div>
                        ))}
                         {(!selectedRegisterDetails?.payment_methods || Object.keys(selectedRegisterDetails.payment_methods).length === 0) && (
                            <div className="p-4 text-center text-muted-foreground text-sm">No hay información detallada</div>
                        )}
                    </div>
                </div>
            </div>
        </DialogContent>
      </Dialog>

      {/* Sales by Warehouse (New) */}
      {salesByWarehouse.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {salesByWarehouse.map((wh, idx) => (
                  <Card key={idx} className="bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
                      <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-blue-800 dark:text-blue-300 flex items-center gap-2">
                              <Package className="h-4 w-4" />
                              {wh.warehouse_name}
                          </CardTitle>
                      </CardHeader>
                      <CardContent>
                          <div className="text-2xl font-bold font-mono tracking-tight text-blue-700 dark:text-blue-400">
                              {formatCLP(wh.total_sales)}
                          </div>
                          <p className="text-xs text-blue-600/80 dark:text-blue-400/80 mt-1">
                              {wh.transaction_count} ventas hoy
                          </p>
                      </CardContent>
                  </Card>
              ))}
          </div>
      )}

      {/* Recent Sales */}
      <Card className="col-span-1 md:col-span-2" data-testid="recent-sales-card">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold">Ventas Recientes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {recentSales.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No hay ventas recientes</p>
            ) : (
              recentSales.map((sale) => (
                <div
                  key={sale.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  data-testid="sale-item"
                >
                  <div>
                    <p className="font-medium">{sale.items.length} producto(s)</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(sale.created_at).toLocaleString('es-CL')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-bold text-lg">{formatCLP(sale.total)}</p>
                    <p className="text-xs text-muted-foreground capitalize">{sale.payment_method}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}